import { Bars3BottomLeftIcon, Bars3BottomRightIcon, Bars3Icon, BoldIcon, ItalicIcon, LinkIcon, StrikethroughIcon } from '@heroicons/react/24/outline';
import { BubbleMenu, Editor } from '@tiptap/react';
import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/core/Button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogHeaderDefault } from '@/components/core/Dialog';
import { Icon } from '@/components/core/Icon';
import { Input } from '@/components/core/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/core/Select';
import { FONT_SIZE, HEADING_LEVELS } from '@/constants';
import { useTranslation } from '@/hooks/useTranslation';

enum Level {
    H1 = 1,
    H2 = 2,
    H3 = 3,
    H4 = 4,
    H5 = 5,
    H6 = 6,
}
interface SetLinkDialogProps {
    onSetButtonClick: (newUrl: string) => void;
    open: boolean;
    setOpen: (open: boolean) => void;
}
const EditorBubbleMenu = ({ editor }: { editor: Editor | null }) => {

    const [isSetLinkDialogOpen, setIsSetLinkDialogOpen] = useState(false);
    const [showHeadingLevel, setShowHeadingLevel] = useState(false);
    const [showBoldButton, setShowBoldButton] = useState(false);

    const setLink = useCallback((newUrl: string) => {

        if (editor === null) {
            return;
        }

        if (newUrl === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink()
                .run();

        } else {
            editor.chain().focus().extendMarkRange('link').setLink({ href: newUrl })
                .run();
        }

        setIsSetLinkDialogOpen(false);
    }, [editor]);

    useEffect(() => {
        const checkHeadingNode = () => {
            if (editor) {
                const node = editor.state.selection.$anchor.node();
                setShowBoldButton(node.type.name !== 'heading');
                setShowHeadingLevel(node.type.name === 'heading');
            }
        };

        editor?.on('selectionUpdate', checkHeadingNode);
        return () => {
            editor?.off('selectionUpdate', checkHeadingNode);
        };
    }, [editor]);

    // Handle heading level change
    const handleHeadingChange = useCallback(
        (value: string) => {
            const headLine = parseInt(value, 10) as Level;
            editor?.chain().focus().toggleHeading({level:headLine}).run();
        },
        [editor]
    );

    // Handle font size change
    const handleTextSizeChange = useCallback((size: string) => {
        editor?.commands.setFontSize(size);
    }, [editor]);

    if (!editor) {
        return null;
    }

    return (
        <BubbleMenu editor={editor} tippyOptions={{ duration: 180 }}>
            <div className="tw-w-[422px] tw-flex tw-flex-row tw-justify-center tw-p-2 tw-pr-13 tw-shadow tw-z-10 tw-bg-white">
                {showBoldButton && <button
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    disabled= {showHeadingLevel}
                    className={`tw-px-3 tw-border tw-border-solid tw-border-gray-200
                        ${editor.isActive('bold') ? 'tw-bg-gray-200' : 'tw-bg-white'} hover:tw-bg-gray-200`}
                >
                    <Icon icon={BoldIcon} size="sm" variant="dark" />
                </button>}
                <button
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className={`tw-px-3 tw-border tw-border-solid tw-border-gray-200
                        ${editor.isActive('italic') ? 'tw-bg-gray-200' : 'tw-bg-white '} hover:tw-bg-gray-200`}
                >
                    <Icon icon={ItalicIcon} size="sm" variant="dark" />
                </button>
                <button
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    className={`tw-px-3 tw-border tw-border-solid tw-border-gray-200
                        ${editor.isActive('strike') ? 'tw-bg-gray-200' : 'tw-bg-white'} hover:tw-bg-gray-200`}
                >
                    <Icon icon={StrikethroughIcon} size="sm" variant="dark" />
                </button>
                <div>
                    <input
                        type="color"
                        onInput={event => editor.chain().focus().setColor((event.target as HTMLInputElement).value).run()}
                        value={editor.getAttributes('textStyle').color}
                        className=
                            "tw-h-full tw-bg-white hover:tw-bg-gray-200 tw-px-3 tw-border tw-border-solid tw-border-gray-200 tw-cursor-pointer"
                    />
                </div>
                <button
                    onClick={() => setIsSetLinkDialogOpen(true)}
                    className={`tw-px-3 tw-border tw-border-solid tw-border-gray-200
                        ${editor.isActive('link') ? 'tw-bg-gray-200' : 'tw-bg-white'} hover:tw-bg-gray-200`}>
                    <Icon icon={LinkIcon} size="sm" variant="dark" />
                </button>
                <div className="toolbar tw tw-border-0 tw-border-gray-200 tw-bg-white tw-p-0">
                    <Select onValueChange={handleTextSizeChange} defaultValue="12" aria-label="Select Font Size">
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {FONT_SIZE.map((fontSize) => (
                                <SelectItem key={fontSize} value={fontSize}>
                                    {fontSize}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                {showHeadingLevel && <div className="toolbar tw-border-0 tw-border-gray-200 tw-bg-white tw-p-0">
                    <Select onValueChange={handleHeadingChange} defaultValue="2" aria-label="Select Heading Level">
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {HEADING_LEVELS.map((headingLevel) => (
                                <SelectItem key={headingLevel.value} value={headingLevel.value}>
                                    {headingLevel.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>}
                <button
                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                    className={'tw-px-3 tw-border tw-border-solid tw-border-gray-200 tw-bg-white hover:tw-bg-gray-200'}
                >
                    <Icon icon={Bars3BottomLeftIcon} size="sm" variant="dark" />
                </button>
                <button
                    onClick={() => editor.chain().focus().setTextAlign('center').run()}
                    className={'tw-px-3 tw-border tw-border-solid tw-border-gray-200 tw-bg-white hover:tw-bg-gray-200'}
                >
                    <Icon icon={Bars3Icon} size="sm" variant="dark" />
                </button>
                <button
                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                    className={'tw-px-3 tw-border tw-border-solid tw-border-gray-200 tw-bg-white hover:tw-bg-gray-200'}
                >
                    <Icon icon={Bars3BottomRightIcon} size="sm" variant="dark" />
                </button>
            </div>

            <SetLinkDialog onSetButtonClick={setLink} open={isSetLinkDialogOpen} setOpen={setIsSetLinkDialogOpen}/>
        </BubbleMenu>
    );
};

const SetLinkDialog = ({ onSetButtonClick, open, setOpen } : SetLinkDialogProps) => {

    const { t } = useTranslation();
    const [link, setLink] = useState<string>('');

    useEffect(() => setLink(''), [open]);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent variant="small">
                <DialogHeader>
                    <DialogHeaderDefault>{t('ENHANCED_CONTENT.INSERT_LINK')}</DialogHeaderDefault>
                </DialogHeader>
                <DialogBody padding="none" className="tw-flex tw-px-6 tw-py-3">
                    <Input placeholder={t('ENHANCED_CONTENT.INSERT_LINK')} value={link} onChange={e => setLink(e.target.value)} />
                </DialogBody>
                <DialogFooter>
                    <Button variant="default" type="submit" onClick={() => onSetButtonClick(link)}>
                        {t('ENHANCED_CONTENT.SET_LINK')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { EditorBubbleMenu };
